import React, { useState } from "react";
import { ChoiceButton, PlayButton, useShuffledChoices } from "../../grammar/shared";
import { ArabicLine, ChoiceLabel, Prompt, isArabicOnly } from "./shared";

/**
 * McItem — four-way multiple choice. Variants share one renderer:
 *   ar_en        Arabic word → English meaning
 *   en_ar        English meaning → Arabic word
 *   who_said     a dialogue line → speaker
 *   comprehension  a question about the dialogue
 *   best_reply   a dialogue line → the natural Arabic reply
 * Choices are authored correct-first; useShuffledChoices randomises per item.
 */
export default function McItem({ question, onAnswer, audio }) {
  const { data } = question;
  const [picked, setPicked] = useState(null);
  const view = useShuffledChoices(data.choices, data.correct_index ?? 0, [question.id]);

  const pick = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    onAnswer({ correct: idx === view.correctIndex, answer: view.choices[idx] });
  };

  const showArabic = !!data.prompt_ar;
  return (
    <>
      <Prompt>{question.prompt_en}</Prompt>
      {showArabic && (
        <div className="flex items-center justify-center gap-3">
          <ArabicLine text={data.prompt_ar} size={data.variant === "ar_en" ? "text-5xl" : "text-3xl"} />
          {audio.audioAllowed && <PlayButton text={data.prompt_ar} playingText={audio.playingText} onPlay={audio.play} />}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl mx-auto mt-2">
        {view.choices.map((choice, idx) => {
          let state = "idle";
          if (picked !== null) {
            if (idx === view.correctIndex) state = "correct";
            else if (idx === picked) state = "wrong";
            else state = "dim";
          }
          return (
            <ChoiceButton key={idx} arabic={isArabicOnly(choice)} state={state} disabled={picked !== null} onClick={() => pick(idx)}>
              <ChoiceLabel text={choice} />
            </ChoiceButton>
          );
        })}
      </div>
    </>
  );
}
